import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { Drawer } from '@antimatterhq/ui/components/drawer';
import { Text } from '@antimatterhq/ui/components/typography';
import { Button } from '@antimatterhq/ui/components/button';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@antimatterhq/ui/components/icon';
import { DatasourceUpgradeDiffFixedString } from '../diff/fixedString/DatasourceUpgradeDiffFixedString';
import { DatasourceUpgradeDiffTextarea } from '../diff/textarea/DatasourceUpgradeDiffTextarea';
import { DatasourceUpgradeDiffFieldSpec } from '../diff/fieldSpec/DatasourceUpgradeDiffFieldSpec';
import { DatasourceUpgradeDiffTextbox } from '../diff/textbox/DatasourceUpgradeDiffTextbox';
import { DatasourceUpgradeDiffMultiValue } from '../diff/multiValue/DatasourceUpgradeDiffMultiValue';
import { DatasourceUpgradeDiffPreserveUnreferencedColumns } from '../diff/preserveUnreferenceColumns/DatasourceUpgradeDiffPreserveUnreferencedColumns';
import { DatasourceUpgradeDiffTable } from '../diff/table/DatasourceUpgradeDiffTable';
import { useDatasourceUpgrade } from '@/contexts/DatasourceUpgradeContext';
import {
  isFieldSpecChange,
  isMultiValueFieldChange,
  isPreserveUnreferencedColumnsChange,
  isStringFieldChange,
  isTableChange,
} from '@/lib/datasource/upgrade/utils/datasourceUpgradeUtils';

export type DatasourceUpgradeDrawerProps = {
  onClose: () => void;
  setDrawerHeight?: (height: number) => void;
};

export const DatasourceUpgradeDrawer = ({
  onClose,
  setDrawerHeight,
}: DatasourceUpgradeDrawerProps) => {
  const {
    currentDiff,
    nextDiff,
    previousDiff,
    currentDiffIndex,
    diffs,
    setFieldStatus,
    acceptChanges,
    rejectChanges,
    setChanges,
    hasUnappliedChanges,
    nextUnappliedChange,
    publish,
    isPublishing,
    isSaving,
  } = useDatasourceUpgrade();

  const navigate = useNavigate();
  const drawerRef = useRef<HTMLDivElement>(null);

  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [showCompletedContent, setShowCompletedContent] = useState(false);

  const hasNextDiff = useMemo(() => {
    return currentDiffIndex < diffs.length - 1;
  }, [currentDiffIndex, diffs]);

  useEffect(() => {
    if (!hasUnappliedChanges) {
      setTimeout(() => {
        setShowCompletedContent(true);
      }, 700);
    }
  }, [hasUnappliedChanges]);

  // Measure drawer height when currentDiff changes or content changes
  useEffect(() => {
    if (!setDrawerHeight) return;

    const measureHeight = () => {
      if (drawerRef.current) {
        const height = drawerRef.current.offsetHeight;
        setDrawerHeight(height);
      }
    };

    // Measure immediately
    measureHeight();

    // Use ResizeObserver to detect height changes
    const resizeObserver = new ResizeObserver(() => {
      measureHeight();
    });

    if (drawerRef.current) {
      resizeObserver.observe(drawerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    currentDiff,
    setDrawerHeight,
    isAccepting,
    isRejecting,
    isApplying,
    hasUnappliedChanges,
  ]);

  const goToNextUnappliedChange = useCallback(() => {
    setTimeout(() => {
      setIsAccepting(false);
      setIsRejecting(false);
      setIsApplying(false);
      nextUnappliedChange();
    }, 700);
  }, [nextUnappliedChange]);

  const onNextDiff = useCallback(() => {
    if (hasNextDiff) {
      nextDiff();
      return;
    }
    setShowCompletedContent(true);
  }, [hasNextDiff, nextDiff, setShowCompletedContent]);

  const onPreviousDiff = useCallback(() => {
    if (showCompletedContent) {
      setShowCompletedContent(false);
      return;
    }
    previousDiff();
  }, [showCompletedContent, previousDiff]);

  const onAccept = useCallback(() => {
    setIsAccepting(true);
    setFieldStatus(currentDiff, 'confirmed');
    goToNextUnappliedChange();

    if (isFieldSpecChange(currentDiff)) {
      acceptChanges(currentDiff);
      return;
    }

    if (isStringFieldChange(currentDiff)) {
      setChanges({
        fieldDiff: currentDiff,
        value: currentDiff.upgradePresetValue,
      });
      return;
    }

    if (isPreserveUnreferencedColumnsChange(currentDiff)) {
      acceptChanges(currentDiff);
      return;
    }

    if (isTableChange(currentDiff)) {
      acceptChanges(currentDiff);
      return;
    }

    throw new Error(`Unhandled diff type for onAccept ${currentDiff.type}`);
  }, [
    currentDiff,
    setFieldStatus,
    acceptChanges,
    setChanges,
    goToNextUnappliedChange,
  ]);

  const onReject = useCallback(() => {
    setIsRejecting(true);
    setFieldStatus(currentDiff, 'confirmed');
    goToNextUnappliedChange();

    if (isFieldSpecChange(currentDiff)) {
      rejectChanges(currentDiff);
      return;
    }

    if (isStringFieldChange(currentDiff)) {
      setChanges({
        fieldDiff: currentDiff,
        value: currentDiff.currentValue,
      });
      return;
    }

    if (isPreserveUnreferencedColumnsChange(currentDiff)) {
      rejectChanges(currentDiff);
      return;
    }

    if (isTableChange(currentDiff)) {
      rejectChanges(currentDiff);
      return;
    }

    throw new Error(`Unhandled diff type for onReject ${currentDiff.type}`);
  }, [
    currentDiff,
    setFieldStatus,
    rejectChanges,
    setChanges,
    goToNextUnappliedChange,
  ]);

  const onApply = useCallback(
    (value: string | string[]) => {
      setIsApplying(true);
      goToNextUnappliedChange();
      setFieldStatus(currentDiff, 'confirmed');
      setChanges({
        fieldDiff: currentDiff,
        value,
      });
    },
    [currentDiff, setChanges, setFieldStatus, goToNextUnappliedChange],
  );

  const remainingDiffCount = useMemo(() => {
    return diffs.filter((q) => q.status !== 'confirmed').length;
  }, [diffs]);

  const onPublish = useCallback(() => {
    publish({
      onSuccess: () => {
        navigate('/configure/data-sources');
      },
    });
  }, [publish, navigate]);

  return (
    <Drawer.Root data-testid="datasource-upgrade-drawer" ref={drawerRef}>
      <div className="flex justify-between py-2 pl-4">
        {!showCompletedContent && (
          <Text
            bold
            size="base"
            data-testid="datasource-upgrade-title"
          >{`${currentDiffIndex + 1}. ${currentDiff.description}`}</Text>
        )}
        {showCompletedContent && <div></div>}
        <div className="flex items-center gap-2">
          {isSaving && (
            <div className="mr-2 flex items-center gap-2">
              <Text
                color="info"
                className="italic"
                data-testid="datasource-upgrade-saving-label"
              >
                Saving
              </Text>
              <Icon
                icon="lucide:loader-circle"
                className="animate-spin text-grey-500 dark:text-grey-400"
                data-testid="datasource-upgrade-saving-icon"
              />
            </div>
          )}
          {!showCompletedContent && (
            <Text data-testid="datasource-upgrade-reviewing-label">
              Reviewing {currentDiffIndex + 1} of {diffs.length}
            </Text>
          )}
          <Drawer.Close
            onClose={onClose}
            data-testid="datasource-upgrade-close"
            disabled={isSaving}
          />
        </div>
      </div>
      <Drawer.Content
        collapsed={false}
        data-testid="datasource-upgrade-content"
      >
        <div className="p-4">
          {showCompletedContent && (
            <>
              <div className="flex flex-1 items-center justify-center gap-2">
                <Text bold>All changes have been applied.</Text>
                <Icon
                  icon="lucide:check-circle"
                  className="text-green-500"
                  data-testid="datasource-upgrade-completed-icon"
                />
              </div>
              <div className="flex justify-center">
                <Text>
                  Click &quot;Apply changes&quot; to publish the changes to your
                  datasource.
                </Text>
              </div>
            </>
          )}
          {!showCompletedContent && (
            <>
              {isStringFieldChange(currentDiff) &&
                currentDiff.inputType === 'fixed' && (
                  <DatasourceUpgradeDiffFixedString
                    diff={currentDiff}
                    onAccept={onAccept}
                    onReject={onReject}
                    isAccepting={isAccepting}
                    isRejecting={isRejecting}
                  />
                )}
              {isStringFieldChange(currentDiff) &&
                currentDiff.inputType === 'textarea' && (
                  <DatasourceUpgradeDiffTextarea
                    diff={currentDiff}
                    onApply={onApply}
                    isApplying={isApplying}
                  />
                )}
              {isStringFieldChange(currentDiff) &&
                currentDiff.inputType === 'textbox' && (
                  <DatasourceUpgradeDiffTextbox
                    diff={currentDiff}
                    onApply={onApply}
                    isApplying={isApplying}
                  />
                )}
              {isFieldSpecChange(currentDiff) && (
                <DatasourceUpgradeDiffFieldSpec
                  diff={currentDiff}
                  onAccept={onAccept}
                  onReject={onReject}
                  isAccepting={isAccepting}
                  isRejecting={isRejecting}
                />
              )}
              {isMultiValueFieldChange(currentDiff) && (
                <DatasourceUpgradeDiffMultiValue
                  diff={currentDiff}
                  onApply={onApply}
                  isApplying={isApplying}
                />
              )}
              {isPreserveUnreferencedColumnsChange(currentDiff) && (
                <DatasourceUpgradeDiffPreserveUnreferencedColumns
                  diff={currentDiff}
                  onAccept={onAccept}
                  onReject={onReject}
                  isAccepting={isAccepting}
                  isRejecting={isRejecting}
                />
              )}
              {isTableChange(currentDiff) && (
                <DatasourceUpgradeDiffTable
                  diff={currentDiff}
                  onAccept={onAccept}
                  onReject={onReject}
                  isAccepting={isAccepting}
                  isRejecting={isRejecting}
                />
              )}
            </>
          )}
          <div className="flex flex-1 justify-between pt-4">
            <div className="flex flex-1 justify-end gap-2">
              <Button
                variant="default"
                disabled={currentDiffIndex === 0}
                onClick={onPreviousDiff}
                leftIcon="lucide:chevron-left"
                data-testid="datasource-upgrade-previous-btn"
              >
                Previous
              </Button>
              <Button
                variant="default"
                onClick={onNextDiff}
                rightIcon="lucide:chevron-right"
                disabled={
                  (!hasNextDiff && hasUnappliedChanges) || showCompletedContent
                }
                data-testid="datasource-upgrade-next-btn"
              >
                Next
              </Button>
              {hasUnappliedChanges && (
                <Button
                  variant="default"
                  onClick={nextUnappliedChange}
                  data-testid="datasource-upgrade-next-pending-btn"
                >
                  Next pending change ({remainingDiffCount})
                </Button>
              )}
              {!hasUnappliedChanges && (
                <Button
                  variant="primary"
                  onClick={onPublish}
                  loading={isPublishing}
                  data-testid="datasource-upgrade-apply-btn"
                  disabled={isPublishing || isSaving}
                >
                  Apply changes
                </Button>
              )}
            </div>
          </div>
        </div>
      </Drawer.Content>
    </Drawer.Root>
  );
};
